import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Button } from 'antd';
import type { Club } from '@/types/models';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { apiService } from '@/services/api.service';
import { logger } from '@/utils/logger';

interface InviteManagerModalProps {
  visible: boolean;
  clubs: Club[];
  onCancel: () => void;
  onSuccess: () => void;
  initialClubId?: number;
}

const InviteManagerModal: React.FC<InviteManagerModalProps> = ({
  visible,
  clubs,
  onCancel,
  onSuccess,
  initialClubId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (initialClubId) {
        form.setFieldsValue({ clubId: initialClubId });
      }
    }
  }, [visible, form, initialClubId]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await apiService.post(API_ENDPOINTS.CLUB_MANAGERS.INVITATIONS, {
        clubId: values.clubId,
        email: values.email,
        role: values.role || 'manager',
      });

      message.success('Приглашение отправлено на email');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      logger.error('Error sending invitation:', error);
      message.error(error.response?.data?.message || 'Ошибка при отправке приглашения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Пригласить менеджера по email"
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ role: 'manager' }}>
        <Form.Item
          name="clubId"
          label="Клуб"
          rules={[{ required: true, message: 'Выберите клуб' }]}
        >
          <Select placeholder="Выберите клуб" allowClear>
            {clubs.map((club) => (
              <Select.Option key={club.id} value={club.id}>
                {club.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Введите email' },
            { type: 'email', message: 'Введите корректный email' },
          ]}
        >
          <Input placeholder="email@example.com" />
        </Form.Item>

        <Form.Item name="role" label="Роль">
          <Select>
            <Select.Option value="manager">Менеджер</Select.Option>
            <Select.Option value="head_manager">Главный менеджер</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={onCancel} style={{ marginRight: 8 }}>
            Отмена
          </Button>
          <Button type="primary" onClick={() => form.submit()} loading={loading}>
            Отправить приглашение
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InviteManagerModal;
